import { apiWithLoading, apiBackground } from './api'
import type { CampaignKpis, CampaignProgressKpis } from '../utils/campaignKpis'

/** #320 — `?tenantId=` seulement quand un tenant est désigné (compte système). */
const tenantScope = (tenantId?: string) => (tenantId ? { params: { tenantId } } : undefined)

export interface Campaign {
  id: string
  name: string
  description?: string
  callScript?: string  // ✅ AJOUTÉ - Script d'appel avec variables [Name], [Company], etc.
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'FINISHED'
  startDate?: string
  endDate?: string
  createdAt: string
  updatedAt: string
  // Computed fields from _count
  contactCount?: number
  callCount?: number
  // Agrégats servis par GET /campaigns/:id (findOne) — LOT-CAMPAGNES (C-4)
  contactStats?: Record<string, number>
  totalCallDuration?: number
  /**
   * #366 — Progression (et, sur `GET /campaigns/:id`, Conversion) : définition
   * UNIQUE calculée par le serveur (`campaign-kpis.ts`). La liste ne sert que
   * la progression ; la fiche sert les deux.
   */
  kpis?: CampaignProgressKpis | CampaignKpis
  // ✅ Quick SMS feature
  quickSmsEnabled?: boolean
  quickSmsTemplateId?: string
  quickSmsTemplate?: {
    id: string
    name: string
    content: string
    category?: string
  }
  // ✅ Quick Email feature
  quickEmailEnabled?: boolean
  quickEmailTemplateId?: string
  quickEmailTemplate?: {
    id: string
    name: string
    subject: string
    content: string
    category?: string
  }
  // Manual call settings
  requireContactForManualCall?: boolean
  /** #253 — « Collecte de dons » : association AlloDons de la campagne ; `null` = aucune. */
  allodonsConnectionId?: string | null
  /** Organisation propriétaire (servie par le serveur) — L-NAV-3 : le compte système s'en sert pour désigner le bon tenant. */
  tenantId?: string
  // L-NAV-3 — réglages d'appel (onglet Paramètres, ex-/admin/campaigns/:id/settings).
  maxCallsPerContact?: number
  retryDelayMinutes?: number
  autoDialDelaySeconds?: number
  callStartHour?: number
  callEndHour?: number
  allowWeekends?: boolean
}

export interface CreateCampaignDto {
  name: string
  description?: string
  callScript?: string  // ✅ AJOUTÉ
  status?: Campaign['status']
  startDate?: string
  endDate?: string
  // Quick SMS fields
  quickSmsEnabled?: boolean
  quickSmsTemplateId?: string
  // Quick Email fields
  quickEmailEnabled?: boolean
  quickEmailTemplateId?: string
  // Manual call settings
  requireContactForManualCall?: boolean
  /** #253 — « Collecte de dons » : association AlloDons de la campagne ; `null` = aucune. */
  allodonsConnectionId?: string | null
  /** #393 — délai de préview avant l'appel suivant (s) ; envoyé à la création (défaut 8). */
  autoDialDelaySeconds?: number
}

export interface UpdateCampaignDto extends Partial<CreateCampaignDto> {}

/** `GET /campaigns/:id/stats` (#366 : KPI uniques + appels au statut ANSWERED). */
export interface CampaignStats {
  totalContacts: number
  totalCalls: number
  /** Décroché technique (messageries comprises) — pas une conversion. */
  answeredCalls: number
  kpis: CampaignKpis
}

export class CampaignsService {
  private static readonly BASE_PATH = '/campaigns'

  /** #367 — taille d'une page (= `CAMPAIGNS_PAGE_MAX` du serveur). */
  static readonly PAGE_SIZE = 500
  /** Garde-fou : au plus 40 pages (20 000 campagnes) — jamais une boucle sans fin. */
  static readonly MAX_PAGES = 40

  // Get all campaigns
  // #367 — TOUTES les campagnes : le serveur pagine (50 par défaut) et cet appel
  // n'envoyait rien — au-delà de 50, les plus anciennes disparaissaient de la
  // liste, des compteurs et de la recherche. On lit les pages de 500 jusqu'à
  // une page incomplète, dédoublonnées par id.
  static async getAll(): Promise<Campaign[]> {
    const byId = new Map<string, any>()
    for (let page = 1; page <= CampaignsService.MAX_PAGES; page += 1) {
      const response = await apiWithLoading.get(
        CampaignsService.BASE_PATH, // ✅ Use class name instead of 'this' for React Query
        'Loading campaigns...',
        { params: { page, limit: CampaignsService.PAGE_SIZE } }
      )
      const rows: any[] = Array.isArray(response.data) ? response.data : []
      for (const campaign of rows) byId.set(campaign.id, campaign)
      if (rows.length < CampaignsService.PAGE_SIZE) break
    }

    // Map _count to contactCount/callCount for UI compatibility
    return [...byId.values()].map((campaign: any) => ({
      ...campaign,
      contactCount: campaign._count?.campaignContacts || 0,
      callCount: campaign._count?.calls || 0
    }));
  }

  // Get single campaign
  // LOT-CAMPAGNES (C-7①) : client de fond — le chargement est porté par le
  // squelette de la page (React Query), pas par le voile global ; et les cinq
  // console.log de debug (payload complet inclus) sont retirés.
  static async getOne(id: string): Promise<Campaign> {
    const response = await apiBackground.get(`${CampaignsService.BASE_PATH}/${id}`)
    return response.data
  }

  // Get campaign by ID
  static async getById(id: string): Promise<Campaign> {
    const response = await apiWithLoading.get(
      `${CampaignsService.BASE_PATH}/${id}`,
      'Loading campaign...'
    )
    return response.data
  }

  // Get campaign by ID (background - no loader)
  static async getByIdBackground(id: string): Promise<Campaign> {
    const response = await apiBackground.get(`${CampaignsService.BASE_PATH}/${id}`)
    return response.data
  }

  // Alias for getCampaign
  static async getCampaign(id: string): Promise<Campaign> {
    return CampaignsService.getOne(id)
  }

  // Create new campaign
  static async create(campaign: CreateCampaignDto): Promise<Campaign> {
    const response = await apiWithLoading.post(
      CampaignsService.BASE_PATH,
      campaign,
      'Creating campaign...'
    )
    return response.data
  }

  // Update campaign
  static async update(id: string, campaign: UpdateCampaignDto): Promise<Campaign> {
    const response = await apiWithLoading.patch(
      `${CampaignsService.BASE_PATH}/${id}`,
      campaign,
      'Updating campaign...'
    )
    return response.data
  }

  // Delete campaign
  static async delete(id: string, tenantId?: string): Promise<void> {
    const url = tenantId 
      ? `${CampaignsService.BASE_PATH}/${id}?tenantId=${tenantId}`
      : `${CampaignsService.BASE_PATH}/${id}`
    await apiWithLoading.delete(
      url,
      'Deleting campaign...'
    )
  }

  // Get campaign statistics
  static async getStats(id: string): Promise<CampaignStats> {
    const response = await apiWithLoading.get(
      `${CampaignsService.BASE_PATH}/${id}/stats`,
      'Loading campaign statistics...'
    )
    return response.data
  }

  // Start campaign
  static async start(id: string): Promise<Campaign> {
    return CampaignsService.update(id, { status: 'ACTIVE' })
  }

  // Pause campaign
  static async pause(id: string): Promise<Campaign> {
    return CampaignsService.update(id, { status: 'PAUSED' })
  }

  // Finish campaign
  static async finish(id: string): Promise<Campaign> {
    return CampaignsService.update(id, { status: 'FINISHED' })
  }

  // Get campaign groups with stats
  // #320 (L-NAV-7) — `tenantId` : tenant DE LA CAMPAGNE, à désigner par le
  // seul compte système (sans lui, le serveur lui donne un tenant `null` :
  // aucun filtre, et `unassign-all` toucherait les agents de TOUS les clients).
  static async getGroups(campaignId: string, tenantId?: string): Promise<GroupStats[]> {
    const response = await apiWithLoading.get(
      `${CampaignsService.BASE_PATH}/${campaignId}/groups`,
      'Loading groups...',
      tenantScope(tenantId)
    )
    return response.data
  }

  // Assign groups to an agent
  static async assignGroupsToAgent(
    campaignId: string,
    userId: string,
    groups: Array<{ group: string; priority: number }>,
    tenantId?: string
  ): Promise<{ success: boolean; assigned: number }> {
    const response = await apiWithLoading.post(
      `${CampaignsService.BASE_PATH}/${campaignId}/assign-groups`,
      { userId, groups },
      'Assigning groups...',
      tenantScope(tenantId)
    )
    return response.data
  }

  // Bulk: assign ONE group to several agents at once (LOT-ASSIGNATIONS)
  static async bulkAssignGroup(
    campaignId: string,
    group: string,
    userIds: string[],
    tenantId?: string
  ): Promise<{ success: boolean; assigned: number; skipped: number }> {
    const response = await apiWithLoading.post(
      `${CampaignsService.BASE_PATH}/${campaignId}/groups/bulk-assign`,
      { group, userIds },
      'Assigning group...',
      tenantScope(tenantId)
    )
    return response.data
  }

  // Bulk: unassign every agent from a group (LOT-ASSIGNATIONS)
  static async unassignAllFromGroup(
    campaignId: string,
    group: string,
    tenantId?: string
  ): Promise<{ success: boolean; removed: number }> {
    const response = await apiWithLoading.post(
      `${CampaignsService.BASE_PATH}/${campaignId}/groups/unassign-all`,
      { group },
      'Unassigning agents...',
      tenantScope(tenantId)
    )
    return response.data
  }
}

export interface GroupStats {
  group: string
  contactCount: number
  availableCount: number
  agentCount: number
  agents: Array<{
    id: string
    name: string
    email: string
    priority: number
  }>
}

export default CampaignsService
